import { Warning } from "@phosphor-icons/react"
import type { ValueGetterParams } from "ag-grid-community"
import { min, sum, sumBy } from "lodash"

import { QualityCellRenderer } from "~/components/forecastEvaluation/QualityCellRenderer"
import { DemandInfoBadge } from "~/components/reusable/DemandInfoBadge"
import { DemandAlertCell } from "~/components/reusable/DemandAlertCell"
import { INTERACTIVE_CELL_CLASS } from "~/components/reusable/InteractiveCell"
import { NpiForecastValue } from "~/components/reusable/NpiForecastValue"
import { Tooltip } from "~/components/reusable/Tooltip"
import {
  FORECAST_QUALITY_LABELS,
  ForecastQualityLevel,
  getAggregateForecastQualityLabel,
} from "~/constants/forecastEvaluation"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { getFilteredLeafData } from "~/utils/ag-utils"
import type { RouterOutputs } from "~/utils/api"
import {
  compareWithNullsLast,
  createAverageColumn,
  createNumberColumn,
  createStringColumn,
  createSumColumn,
  createUniqueStringColumn,
} from "~/utils/columnTemplates"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { ALERT_NO_ALERT, getAlerts, getInfoValue } from "~/utils/forecast-alert-utils"
import {
  salesVsLyByWindow,
  salesVsLyCoverageNote,
  type SalesVsLyWindow,
} from "~/utils/forecast-decomposition"
import { formatNumber, roundForecast } from "~/utils/formatting"
import { expectedGrowthFraction, expectedGrowthUnits } from "~/utils/growthMetrics"
import { isLifecyclePhasedOut } from "~/utils/lifecycleUtils"
import { formatName } from "~/utils/namingUtils"
import { extractVariantSummaries, ProductTableColumn } from "~/utils/productTableUtils"

import {
  blankWhenNull,
  leafVariantIds,
  mutedWhenPhasedOut,
  phasedOutTooltip,
  tooltipOrField,
} from "./cellHelpers"
import { salesWithoutPreOrders } from "./sales"
import type { ColumnSpec } from "./types"

export type ForecastMetadata = { modelId: string; modelName: string }[] | undefined

export type ForecastEvaluationMap = RouterOutputs["forecastEvaluation"]["forecastEvaluationMap"]

/** Scales a variant's gross demand to net; null while net factors load. */
export type NetForecastValueGetter = (variantId: string, gross: number) => number | null

export interface ForecastColumnCtx {
  unfulfillablePreordersFlag: boolean | undefined
}

export type ForecastGridContext = {
  /** Demand alerts are company-wide, so a channel- or warehouse-scoped table has none. */
  alertsUnavailable: boolean
  forecastMetadata: ForecastMetadata
  netForecastMode: boolean
  netForecastValueGetter: NetForecastValueGetter | undefined
  netForecastPending: boolean
  forecastAlertThreshold: number
  evaluationMap: ForecastEvaluationMap | undefined
}

type ForecastSpec = ColumnSpec<IVariantSummary, ForecastColumnCtx, ForecastGridContext>

/** AG Grid types a colDef callback's `context` as `any`. */
export const forecastGrid = (params: { context: unknown }) => params.context as ForecastGridContext

const PHASED_OUT_NO_DEMAND = "Phased out. No demand generated"

const numericAsc = (a: number, b: number) => a - b

const localeAsc = (a: string, b: string) => a.localeCompare(b)

/** The alert text a cell sorts by; an alert-free cell has none. */
const alertOrNull = (value: unknown) => (!value || value === ALERT_NO_ALERT ? null : String(value))

/** Numeric sort that keeps rows without a value at the bottom in both directions. */
function compareGrowth(
  valueA: unknown,
  valueB: unknown,
  _ctx: ForecastColumnCtx,
  isDesc: boolean,
): number {
  return compareWithNullsLast(valueA as number | null, valueB as number | null, isDesc, numericAsc)
}

/** Whole demand units, matching the rounding the forecast columns have always shown. */
function formatDemand(value: unknown): string {
  return formatNumber(roundForecast(value as number))
}

/** The forecast model's display name, falling back to its raw id. */
export function forecastModelName(
  row: IVariantSummary,
  forecastMetadata: ForecastMetadata,
): string {
  return forecastMetadata?.find((x) => x.modelId == row.model)?.modelName ?? row.model ?? "-"
}

/** Forecast demand with every pre-order removed, known and unknown alike. */
export function forecastWithoutPreOrders(row: IVariantSummary): number {
  const forecast = row.forecastQuantity ?? 0
  const knownPreOrders =
    row.futureKnownPreorders?.reduce((total, po) => total + po.quantity, 0) ?? 0
  const unknownPreOrders = row.forecastAlertAggregate?.totalUnknownPreOrders ?? 0
  return forecast - knownPreOrders - unknownPreOrders
}

function preorderQuantity(
  row: IVariantSummary,
  pick: (po: NonNullable<IVariantSummary["futureKnownPreorders"]>[number]) => number,
): number | null {
  const preorders = row.futureKnownPreorders ?? []
  if (preorders.length === 0) return null
  const qty = sum(preorders.map(pick))
  return qty > 0 ? qty : null
}

export const FORECAST_MODEL_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.ForecastModel,
  calculate: (row, _ctx, grid) => forecastModelName(row, grid.forecastMetadata),
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.ForecastModel,
      tooltipTitle: "Forecast models",
      uniqueSuffix: "models",
    }),
}

export const FORECASTED_GROWTH_UNITS_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.ForecastedGrowthUnits,
  calculate: (row) => expectedGrowthUnits(row.salesQuantity ?? 0, row.forecastQuantity ?? 0),
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.ForecastedGrowthUnits,
      // Same null test the default formatter renders "-" for, so the dash always carries its reason.
      tooltip: (params) =>
        params.value == null
          ? {
              unavailableMessage: `No ${formatName("sale", true)} in the last 52 weeks and no expected demand in the next 52.`,
            }
          : leafVariantIds(params),
      tooltipComponentParams: { tooltipType: "SalesAndForecast", type: "forecast" },
    }),
}

export const UNFULFILLABLE_PREORDER_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.UnfulfillablePreorder,
  calculate: (row) => preorderQuantity(row, (po) => po.unfulfillableQty),
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.UnfulfillablePreorder,
      valueFormatter: blankWhenNull,
      tooltip: leafVariantIds,
      tooltipComponentParams: { tooltipType: "PreOrders" },
    }),
}

export const AT_RISK_PREORDER_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.AtRiskPreorder,
  calculate: (row) => preorderQuantity(row, (po) => po.atRiskQty),
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.AtRiskPreorder,
      valueFormatter: blankWhenNull,
      tooltip: leafVariantIds,
      tooltipComponentParams: { tooltipType: "PreOrders" },
    }),
}

export const FORECAST_WITHOUT_KNOWN_PRE_ORDERS_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.ForecastWithoutKnownPreOrders,
  calculate: forecastWithoutPreOrders,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.ForecastWithoutKnownPreOrders,
      minWidth: 180,
      valueFormatter: (params) => formatDemand(params.value),
      tooltip: leafVariantIds,
      tooltipComponentParams: { tooltipType: "SalesAndForecast", type: "forecast" },
    }),
}

export const FORECAST_WITHOUT_PRE_ORDERS_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.ForecastWithoutPreOrders,
  calculate: forecastWithoutPreOrders,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.ForecastWithoutPreOrders,
      minWidth: 180,
      cellClass: mutedWhenPhasedOut,
      valueFormatter: (params) =>
        isLifecyclePhasedOut(extractVariantSummaries(params)) ? "N/A" : formatDemand(params.value),
      tooltip: phasedOutTooltip(PHASED_OUT_NO_DEMAND, leafVariantIds),
      tooltipComponentParams: { tooltipType: "SalesAndForecast", type: "forecast" },
    }),
}

export const STOCKOUT_IMPUTATION_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.StockoutImputation,
  calculate: (row) => row.forecastAlertAggregate?.stockoutTotal || undefined,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.StockoutImputation,
    }),
}

export const FORECAST_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.Forecast,
  render: (params) => {
    if (params.context.netForecastMode && params.context.netForecastPending) return "-"
    if (params.node?.footer) return formatDemand(params.value)
    const entries = extractVariantSummaries(params)
    if (isLifecyclePhasedOut(entries)) return "N/A"
    const npiEntries = entries.filter((x) => x.isNpi)

    return (
      <NpiForecastValue
        value={formatDemand(params.value)}
        npiCount={npiEntries.length}
        totalCount={entries.length}
        isVariantLevel={!!params.node?.group}
      />
    )
  },
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.Forecast,
      field: "forecastQuantity",
      // Not `headerName`: the table matches row groups on it, and the chooser keeps "Demand".
      headerValueGetter: (p) => (forecastGrid(p).netForecastMode ? "Net Demand" : "Demand"),
      // Sum leaves via valueGetter (not aggFunc) so AG Grid flashes the cell on demand changes;
      // the footer returns undefined so aggFunc still totals it.
      valueGetter: (p) => {
        if (p.node?.footer) return undefined
        const { netForecastMode, netForecastValueGetter } = forecastGrid(p)
        const leafValue = (e: IVariantSummary) =>
          netForecastMode && netForecastValueGetter
            ? (netForecastValueGetter(e.variantId, e.forecastQuantity ?? 0) ?? 0)
            : (e.forecastQuantity ?? 0)
        return extractVariantSummaries(p).reduce((acc, e) => acc + leafValue(e), 0)
      },
      headerTooltip: (p) =>
        forecastGrid(p).netForecastMode
          ? "Demand × (1 - return rate). Read-only, because adjustments always apply to gross demand."
          : undefined,
      initialWidth: 130,
      cellClass: mutedWhenPhasedOut,
      valueFormatter: (params) => {
        if (isLifecyclePhasedOut(extractVariantSummaries(params))) return "N/A"
        // Never show gross numbers labelled as net while factors load.
        const { netForecastMode, netForecastPending } = forecastGrid(params)
        if (netForecastMode && netForecastPending) return "-"
        return formatDemand(params.value)
      },
      // A gross breakdown tooltip would mislead under a "Net Demand" header.
      tooltip: (params) => {
        if (forecastGrid(params).netForecastMode) return undefined
        return params.node?.group ? params.value : params.data?.forecastQuantity
      },
      tooltipComponentParams: { tooltipType: "SalesAndForecast", type: "forecast" },
    }),
}

export const PRE_ORDERS_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.PreOrders,
  render: (params, ctx) => {
    const formatted = params.valueFormatted ?? params.value ?? ""
    if (!ctx.unfulfillablePreordersFlag) return formatted

    const entries: IVariantSummary[] = extractVariantSummaries(params)
    let redQty = 0
    let yellowQty = 0
    for (const v of entries) {
      for (const po of v.futureKnownPreorders ?? []) {
        if (!po || po.displayColor === "none") continue
        redQty += po.unfulfillableQty
        yellowQty += po.atRiskQty
      }
    }

    if (redQty <= 0 && yellowQty <= 0) return formatted

    const isRed = redQty > 0
    return (
      <span className="inline-flex items-center gap-1">
        {formatted}
        <Warning weight="fill" size={14} className={isRed ? "text-icon-red" : "text-icon-orange"} />
      </span>
    )
  },
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.PreOrders,
      valueGetter: (p) =>
        p.data?.futureKnownPreorders?.reduce((sum, po) => sum + po.quantity, 0) ?? 0,
      tooltip: tooltipOrField(leafVariantIds, "preOrderQuantity"),
      tooltipComponentParams: { tooltipType: "PreOrders" },
    }),
}

/**
 * Why expected growth reads "-", in the order {@link expectedGrowthFraction} rejects: a missing
 * baseline first, then too little history. One message for both would send a planner off to wait
 * for a year of history that a long-dormant variant already has.
 */
function growthUnavailableMessage(salesLastYear: number, minSoldSince: Date | undefined): string {
  const sales = formatName("sale", true)
  if (salesLastYear === 0)
    return `No ${sales} in the last 52 weeks, so there is no baseline to measure growth against.`

  const needsAYear = `Expected growth needs a full year of ${sales} history.`
  if (!minSoldSince) return needsAYear
  return `${needsAYear} ${formatName("sale", true, true)} began in ${formatDate(minSoldSince, DateFormatType.MonthYear)}.`
}

export const FORECASTED_GROWTH_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.ForecastedGrowth,
  calculate: (row) => {
    if (isLifecyclePhasedOut([row])) return undefined
    return expectedGrowthFraction(row.salesQuantity ?? 0, row.forecastQuantity ?? 0, row.soldSince)
  },
  aggregate: (entries) =>
    expectedGrowthFraction(
      sumBy(entries, (entry) => entry.salesQuantity),
      sumBy(entries, (entry) => entry.forecastQuantity),
      min(entries.map((entry) => entry.soldSince)),
    ),
  compare: compareGrowth,
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.ForecastedGrowth,
      cellClass: mutedWhenPhasedOut,
      percentage: true,
      decimals: 1,
      tooltip: phasedOutTooltip("Phased out. No demand to calculate growth from", (params) => {
        const entries = extractVariantSummaries(params)
        const totalLastYearUnits = sumBy(entries, (entry) => entry.salesQuantity)
        const totalNextYearUnits = sumBy(entries, (entry) => entry.forecastQuantity)
        const minSoldSince = min(entries.map((v) => v.soldSince))
        const forecastGrowth = expectedGrowthFraction(
          totalLastYearUnits,
          totalNextYearUnits,
          minSoldSince,
        )
        return forecastGrowth !== undefined
          ? `Expected ${formatName("sale", true)} are ${Math.round(forecastGrowth * 100) === 0 ? "expected to remain stationary" : `expected to ${totalNextYearUnits - totalLastYearUnits > 0 ? "increase" : "decrease"}`} from ${formatNumber(totalLastYearUnits)} to ${formatNumber(totalNextYearUnits)} units over the next 52 weeks.`
          : growthUnavailableMessage(totalLastYearUnits, minSoldSince)
      }),
    }),
}

export const FORECASTED_GROWTH_ALERT_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.ForecastedGrowthAlert,
  calculate: (row, _ctx, grid) => {
    if (grid.alertsUnavailable) return undefined
    const alerts = getAlerts(row, grid.forecastAlertThreshold)
    if (!alerts) return undefined
    return alerts.length > 0 ? alerts.join(", ") : ALERT_NO_ALERT
  },
  aggregate: (entries, _ctx, grid) => {
    if (grid.alertsUnavailable) return ""
    const alertCount = entries.filter((entry) => {
      const alerts = getAlerts(entry, grid.forecastAlertThreshold)
      return alerts && alerts.length > 0
    }).length
    return alertCount > 0 ? `${alertCount} Alerts` : ""
  },
  // "No alert" and the empty string an alert-free group aggregates to sort to the bottom in both
  // directions; ordered alphabetically, "No alert" (N) buries Stockout (S) and Pre-order change
  // (P) under it in ascending.
  compare: (valueA, valueB, _ctx, isDescending) =>
    compareWithNullsLast(alertOrNull(valueA), alertOrNull(valueB), isDescending, localeAsc),
  render: (params) => {
    if (params.context.alertsUnavailable)
      return (
        <Tooltip title="Demand alerts are not available for individual channels/warehouses">
          <div className="text-fg-muted paragraph-base">-</div>
        </Tooltip>
      )
    const value = params.value
    if (params.node?.group) {
      if (!value) return null
      return value.includes("Alerts") ? (
        <div className="text-red-500">{value}</div>
      ) : (
        <div className="text-fg-muted paragraph-base">{value}</div>
      )
    }

    const variant = extractVariantSummaries(params)[0]
    // Not alert eligible: nothing to break down.
    if (!value || !variant) return <div className="text-fg-muted paragraph-base">-</div>
    return (
      <DemandAlertCell
        variant={variant}
        forecastAlertThreshold={params.context.forecastAlertThreshold}
      />
    )
  },
  colDef: () =>
    createStringColumn<IVariantSummary>({
      colId: ProductTableColumn.ForecastedGrowthAlert,
      // The `compare` slot supplies the comparator.
      suppressDefaultComparator: true,
      filter: "agSetColumnFilter",
      sortingOrder: ["desc", "asc", null],
      cellStyle: { display: "flex", alignItems: "center", overflow: "hidden" },
      cellClass: ["stringType", INTERACTIVE_CELL_CLASS],
      filterValueGetter: (p) => {
        const { alertsUnavailable, forecastAlertThreshold } = forecastGrid(p)
        if (alertsUnavailable) return undefined
        if (p.node?.group) {
          const values = new Set<string>()
          p.node.allLeafChildren?.forEach((child) => {
            const alerts = getAlerts(child.data, forecastAlertThreshold)
            alerts?.forEach((v) => values.add(v))
          })
          return [...values]
        }
        return getAlerts(p.data, forecastAlertThreshold)
      },
    }),
}

export const FORECAST_INFO_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.ForecastInfo,
  calculate: (row) => getInfoValue(row),
  aggregate: (entries) => {
    const infoCount = entries.filter((entry) => getInfoValue(entry)).length
    return infoCount > 0 ? `${infoCount} Info` : ""
  },
  render: (params) => {
    const value = params.value
    if (!value) return null
    if (params.node?.group) {
      return <div className="text-fg-muted paragraph-base">{value}</div>
    }
    return <DemandInfoBadge data={params.data} label={value} />
  },
  colDef: () =>
    createStringColumn<IVariantSummary>({
      colId: ProductTableColumn.ForecastInfo,
      filter: "agSetColumnFilter",
      sortingOrder: ["desc", "asc", null],
      cellStyle: { display: "flex", alignItems: "center", overflow: "hidden" },
      filterValueGetter: (p) => {
        if (p.node?.group) {
          const values = new Set<string>()
          p.node.allLeafChildren?.forEach((child) => {
            const val = getInfoValue(child.data)
            if (val) values.add(val)
          })
          return [...values]
        }
        return getInfoValue(p.data)
      },
    }),
}

/** Recent sales against the same window last year, as a fraction. */
function salesVsLyColumn(colId: ProductTableColumn, window: SalesVsLyWindow): ForecastSpec {
  return {
    colId,
    calculate: (row) => {
      if (!row.forecastDecomposition) return undefined
      const { pct } = salesVsLyByWindow([row], window)
      return pct != null ? pct / 100 : undefined
    },
    aggregate: (leaves) => {
      const { pct } = salesVsLyByWindow(leaves, window)
      return pct != null ? pct / 100 : undefined
    },
    colDef: () =>
      createNumberColumn<IVariantSummary>({
        colId,
        // A group cell aggregates only the leaves carrying this window, so it names the population.
        tooltip: (params) =>
          params.node?.group
            ? salesVsLyCoverageNote(
                salesVsLyByWindow(getFilteredLeafData<IVariantSummary>(params.node), window),
              )
            : undefined,
        percentage: true,
        decimals: 1,
      }),
  }
}

export const FORECAST_SALES_VS_LY_1M_COLUMN = salesVsLyColumn(
  ProductTableColumn.ForecastSalesVsLy1m,
  1,
)
export const FORECAST_SALES_VS_LY_3M_COLUMN = salesVsLyColumn(
  ProductTableColumn.ForecastSalesVsLy3m,
  3,
)
export const FORECAST_SALES_VS_LY_6M_COLUMN = salesVsLyColumn(
  ProductTableColumn.ForecastSalesVsLy6m,
  6,
)
export const FORECAST_SALES_VS_LY_12M_COLUMN = salesVsLyColumn(
  ProductTableColumn.ForecastSalesVsLy12m,
  12,
)

export const FORECAST_QUALITY_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.ForecastQuality,
  render: (params) => (
    <QualityCellRenderer {...params} evaluationMap={params.context.evaluationMap} />
  ),
  colDef: () => ({
    ...createAverageColumn<IVariantSummary>({
      colId: ProductTableColumn.ForecastQuality,
      valueGetter: (params) => {
        const variantId = params.data?.variantId
        if (!variantId) return null

        const evaluation = forecastGrid(params).evaluationMap?.get(variantId)
        if (!evaluation) return null
        return evaluation.overallScore != null ? evaluation.overallScore * 100 : null
      },
    }),
    filter: "agSetColumnFilter",
    filterValueGetter: (params: ValueGetterParams<IVariantSummary>) => {
      const { evaluationMap } = forecastGrid(params)
      const scoreOf = (variantId?: string): number | null => {
        if (!variantId) return null
        const score = evaluationMap?.get(variantId)?.overallScore
        return score != null ? score * 100 : null
      }
      const node = params.node
      if (!node) return null

      const parent = node.parent
      const groupNode = node.group ? node : parent?.group && parent.level >= 0 ? parent : null
      if (groupNode) {
        return getAggregateForecastQualityLabel(
          groupNode.allLeafChildren?.map((child) => scoreOf(child.data?.variantId)) ?? [],
        )
      }

      // Ungrouped leaf: its own single-variant bucket.
      return getAggregateForecastQualityLabel([scoreOf(node.data?.variantId)])
    },
    filterParams: {
      comparator: (a: string | null, b: string | null) => {
        const order = [
          FORECAST_QUALITY_LABELS[ForecastQualityLevel.Low],
          FORECAST_QUALITY_LABELS[ForecastQualityLevel.Moderate],
          FORECAST_QUALITY_LABELS[ForecastQualityLevel.High],
        ]
        const rank = (v: string | null) => {
          const i = order.indexOf(v ?? "")
          return i === -1 ? order.length : i
        }
        return rank(a) - rank(b)
      },
    },
  }),
}

/** Share of pre-ordered units expected to ship; null when nothing is pre-ordered. */
function preOrderFulfillment(
  preorders: NonNullable<IVariantSummary["futureKnownPreorders"]>,
): number | null {
  const totalQuantity = sum(preorders.map((x) => x.quantity))
  if (totalQuantity === 0) return null
  const totalRisk = sum(preorders.map((x) => x.atRiskQty + x.unfulfillableQty))
  const fulfillment = 1 - totalRisk / totalQuantity
  if (isFinite(fulfillment)) return fulfillment
  return null
}

export const PRE_ORDER_FULFILLMENT_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.PreOrderFulfillment,
  aggregate: (entries) => preOrderFulfillment(entries.flatMap((e) => e.futureKnownPreorders ?? [])),
  colDef: () =>
    createAverageColumn<IVariantSummary>({
      colId: ProductTableColumn.PreOrderFulfillment,
      valueGetter: (params) => preOrderFulfillment(params.data?.futureKnownPreorders ?? []),
      percentage: true,
      tooltip: tooltipOrField(leafVariantIds, "preOrderQuantity"),
      tooltipComponentParams: { tooltipType: "PreOrders" },
    }),
}

/** Growth of demand over sales once pre-orders are removed from both, over one or more rows. */
function growthWithoutPreOrders(rows: IVariantSummary[]): number | undefined {
  const salesWithout = sumBy(rows, salesWithoutPreOrders)
  if (salesWithout === 0) return undefined

  const forecastWithout = sumBy(rows, forecastWithoutPreOrders)

  const pct = (forecastWithout - salesWithout) / salesWithout
  return Number.isFinite(pct) ? pct : undefined
}

export const GROWTH_WITHOUT_PRE_ORDERS_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.GrowthWithoutPreOrders,
  calculate: (row) => growthWithoutPreOrders([row]),
  aggregate: growthWithoutPreOrders,
  compare: compareGrowth,
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.GrowthWithoutPreOrders,
      percentage: true,
      decimals: 1,
    }),
}

export const MISSING_SALES_IMPUTATION_COLUMN: ForecastSpec = {
  colId: ProductTableColumn.MissingSalesImputation,
  calculate: (row) => {
    const total = row.forecastAlertAggregate?.missingSalesTotal ?? 0
    return total || undefined
  },
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.MissingSalesImputation,
    }),
}

export const FORECAST_COLUMN_SPECS: readonly ForecastSpec[] = [
  FORECAST_MODEL_COLUMN,
  FORECAST_COLUMN,
  PRE_ORDERS_COLUMN,
  FORECASTED_GROWTH_COLUMN,
  FORECASTED_GROWTH_UNITS_COLUMN,
  FORECASTED_GROWTH_ALERT_COLUMN,
  FORECAST_INFO_COLUMN,
  FORECAST_SALES_VS_LY_1M_COLUMN,
  FORECAST_SALES_VS_LY_3M_COLUMN,
  FORECAST_SALES_VS_LY_6M_COLUMN,
  FORECAST_SALES_VS_LY_12M_COLUMN,
  FORECAST_QUALITY_COLUMN,
  PRE_ORDER_FULFILLMENT_COLUMN,
  UNFULFILLABLE_PREORDER_COLUMN,
  AT_RISK_PREORDER_COLUMN,
  FORECAST_WITHOUT_KNOWN_PRE_ORDERS_COLUMN,
  FORECAST_WITHOUT_PRE_ORDERS_COLUMN,
  GROWTH_WITHOUT_PRE_ORDERS_COLUMN,
  STOCKOUT_IMPUTATION_COLUMN,
  MISSING_SALES_IMPUTATION_COLUMN,
]
